import React from 'react';

interface ScientificMarkerProps {
    cx?: number;
    cy?: number;
    fill?: string;
    stroke?: string;
    shape?: 'circle' | 'square' | 'triangle' | 'cross' | 'star' | 'diamond';
    size?: number;
    outline?: boolean;
    alpha?: number;
    onClick?: () => void;
    // Catch-all for other props passed by Recharts (like x, y payload)
    [key: string]: any;
}

/**
 * Custom marker shapes for scientific plots
 * Supports: circle, square, triangle, diamond, star, cross
 */
export const ScientificMarker: React.FC<ScientificMarkerProps> = ({
    cx,
    cy,
    fill = '#f59e0b',
    stroke,
    shape = 'circle',
    size = 6,
    outline = false,
    alpha = 1,
    onClick,
    ...props
}) => {
    // Robust coordinate handling - Recharts passes cx/cy for screen coords
    // but we might get x/y from other contexts
    const screenX = cx ?? props.x;
    const screenY = cy ?? props.y;

    // Allow 0 values for coordinates - only reject if undefined/NaN
    if (screenX === undefined || screenY === undefined || !Number.isFinite(screenX) || !Number.isFinite(screenY)) {
        return null;
    }

    const r = size;
    const fillColor = outline ? 'transparent' : fill;
    const strokeColor = outline ? fill : (stroke || 'none');
    const strokeWidth = outline ? 1.5 : 0;
    const opacity = alpha;

    const commonProps = {
        fill: fillColor,
        stroke: strokeColor,
        strokeWidth,
        opacity,
        style: { cursor: onClick ? 'pointer' : 'default' },
        onClick
    };

    switch (shape) {
        case 'square':
            return (
                <rect
                    x={screenX - r}
                    y={screenY - r}
                    width={r * 2}
                    height={r * 2}
                    {...commonProps}
                />
            );

        case 'triangle':
            return (
                <polygon
                    points={`${screenX},${screenY - r} ${screenX + r},${screenY + r} ${screenX - r},${screenY + r}`}
                    {...commonProps}
                />
            );

        case 'diamond':
            return (
                <polygon
                    points={`${screenX},${screenY - r} ${screenX + r},${screenY} ${screenX},${screenY + r} ${screenX - r},${screenY}`}
                    {...commonProps}
                />
            );

        case 'star': {
            // 5-pointed star
            const points = [];
            for (let i = 0; i < 10; i++) {
                const angle = (i * 36 - 90) * Math.PI / 180;
                const radius = i % 2 === 0 ? r : r * 0.4;
                points.push(`${screenX + radius * Math.cos(angle)},${screenY + radius * Math.sin(angle)}`);
            }
            return (
                <polygon
                    points={points.join(' ')}
                    {...commonProps}
                />
            );
        }

        case 'cross':
            const thickness = r * 0.3;
            return (
                <g stroke={fill} strokeWidth={thickness * 2} opacity={opacity}>
                    <line x1={screenX - r} y1={screenY - r} x2={screenX + r} y2={screenY + r} />
                    <line x1={screenX + r} y1={screenY - r} x2={screenX - r} y2={screenY + r} />
                </g>
            );

        case 'circle':
        default:
            return (
                <circle
                    cx={screenX}
                    cy={screenY}
                    r={r}
                    {...commonProps}
                />
            );
    }
};

export default ScientificMarker;
